import {CustomDropdownIndicator} from 'components/customer/Product/Appliance/CustomDropdown';
import {CustomNoOptions} from 'components/customer/Product/Appliance/CustomNoOptions';
import {Appliance} from 'components/customer/Product/Appliance/entity/Appliance';
import {useSelectStyle} from 'components/customer/Product/Appliance/helpers/useSelectStyle';
import {useFetchAppliancesQuery} from 'components/customer/Product/Appliance/store/applianceApi';
import {RANGEHOOD} from 'components/customer/Product/store/productApi';
import {useNotificationContext} from 'contexts/NotificationContext';
import {useFormikContext} from 'formik';
import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import RSelect, {ActionMeta, SingleValue} from 'react-select';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';

interface SelectProps {
    name: string;
    type: number;
    callback: (
        name: string,
        value?: string | null,
        options?: Appliance[]
    ) => void;
    showApplianceForm: () => void;
}

interface SelectContextType {
    type?: number;
    showApplianceForm?: () => void;
}

const SelectContext = createContext<SelectContextType>({});

export const useSelectContext = () => useContext(SelectContext);

export interface ApplianceForm {
    appliance: Appliance;
    rangehood_opening_height?: number;
}

export const Select = ({
    type,
    callback,
    name,
    showApplianceForm,
}: SelectProps) => {
    const {values, setFieldValue} = useFormikContext<ApplianceForm>();
    const {messages} = useNotificationContext();

    const isCustomAppliance = values.appliance && values.appliance?.custom;

    const {data: appliances, isLoading} = useFetchAppliancesQuery();

    const placeholder = useMemo(() => {
        if (isCustomAppliance) {
            if (type == RANGEHOOD) {
                return 'Custom rangehood';
            } else {
                return 'Custom appliance';
            }
        }

        if (type == RANGEHOOD) {
            return 'Select a rangehood';
        } else {
            return 'Search...';
        }
    }, [type, isCustomAppliance]);

    const handleChange = useCallback(
        (appliance: SingleValue<Appliance>, meta: ActionMeta<Appliance>) => {
            switch (meta.action) {
                case 'select-option':
                    void setFieldValue(name, 1); // make field valid
                    void setFieldValue('appliance', appliance);
                    callback(name, appliance?.externalCode, appliances);
                    break;
            }
        },
        [type, callback, name, appliances, setFieldValue]
    );

    // using these as we don't use value and label, we use external code
    const getValue = useCallback(
        (appliance: Appliance) => appliance.externalCode ?? '',
        []
    );
    const getLabel = useCallback(
        (appliance: Appliance) => appliance.externalCode || '',
        []
    );
    // using these as we don't use value and label, we use id and name instead

    const [disabled, setDisabled] = useState(true);
    const initialCall = useRef(true);
    useEffect(() => {
        if (initialCall.current && values) {
            if (values.appliance == undefined) {
                void setFieldValue('appliance', {externalCode: null});
            }

            setDisabled(false);
            initialCall.current = false;
        }
    }, [values]);

    const overlay = useMemo(() => {
        if (
            messages &&
            messages.errors &&
            Array.isArray(messages.errors) &&
            messages.errors.length > 0
        ) {
            const error = messages.errors.find((error) =>
                error.fields.includes('appliance')
            );

            if (error) {
                return error.message;
            }
        }
    }, [messages, name]);
    const {style, setError} = useSelectStyle();

    useEffect(() => {
        setError(overlay != undefined);
    }, [overlay]);

    const value = useMemo(() => {
        if (values.appliance && appliances) {
            const appliance = appliances.find(
                (appliance) =>
                    appliance.externalCode == values.appliance.externalCode
            );

            if (appliance) return appliance;
        }

        return null;
    }, [values.appliance, appliances]);

    const appliedOpeningHeightForCode = useRef<string | null>(null);
    useEffect(() => {
        if (isCustomAppliance) {
            appliedOpeningHeightForCode.current = null;
            return;
        }

        if (
            !value?.externalCode ||
            !appliances ||
            typeof value.height !== 'number'
        ) {
            return;
        }

        const heightAlreadyApplied =
            appliedOpeningHeightForCode.current === value.externalCode &&
            values.rangehood_opening_height === value.height;

        if (heightAlreadyApplied) {
            return;
        }

        appliedOpeningHeightForCode.current = value.externalCode;
        callback(name, value.externalCode, appliances);

        if (values.appliance?.height !== value.height) {
            void setFieldValue('appliance', {
                ...values.appliance,
                height: value.height,
            });
        }
    }, [
        value,
        appliances,
        isCustomAppliance,
        callback,
        name,
        setFieldValue,
        values.appliance,
        values.rangehood_opening_height,
    ]);

    return (
        <SelectContext.Provider value={{type, showApplianceForm}}>
            <OverlayTrigger
                className="error-popover"
                placement="top-end"
                overlay={overlay}>
                <div>
                    <RSelect
                        inputId="appliance-select"
                        value={value}
                        isDisabled={disabled || isLoading}
                        options={appliances}
                        isLoading={isLoading}
                        placeholder={placeholder}
                        onChange={handleChange}
                        getOptionValue={getValue}
                        getOptionLabel={getLabel}
                        components={{
                            DropdownIndicator: CustomDropdownIndicator,
                            NoOptionsMessage: CustomNoOptions,
                        }}
                        styles={style}
                    />
                </div>
            </OverlayTrigger>
        </SelectContext.Provider>
    );
};
